// Minimal ISO-3166-1 country picker for the V3 intake.
// Free-form multi-select; the seed list is editable here.

import { useMemo, useState } from 'react';
import { Check, X, ChevronDown } from 'lucide-react';
import clsx from 'clsx';

/** Seeded ~60 country subset. Editable later via dedicated UI. */
export const COUNTRIES: { code: string; name: string }[] = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'MX', name: 'Mexico' },
  { code: 'BR', name: 'Brazil' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'PE', name: 'Peru' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'IE', name: 'Ireland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'ES', name: 'Spain' },
  { code: 'PT', name: 'Portugal' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AT', name: 'Austria' },
  { code: 'PL', name: 'Poland' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'TR', name: 'Türkiye' },
  { code: 'IL', name: 'Israel' },
  { code: 'AE', name: 'UAE' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'EG', name: 'Egypt' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'KE', name: 'Kenya' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'Korea, Rep.' },
  { code: 'CN', name: 'China' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'SG', name: 'Singapore' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'TH', name: 'Thailand' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'PH', name: 'Philippines' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IN', name: 'India' },
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'LB', name: 'Lebanon' },
];

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  className?: string;
}

export default function CountryPicker({ value, onChange, placeholder, className }: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const selected = useMemo(() => new Set(value), [value]);
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return COUNTRIES;
    return COUNTRIES.filter(
      (c) => c.code.toLowerCase().includes(ql) || c.name.toLowerCase().includes(ql),
    );
  }, [q]);

  const toggle = (code: string) => {
    const next = selected.has(code) ? value.filter((c) => c !== code) : [...value, code];
    onChange(next);
  };

  return (
    <div className={clsx('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="input w-full text-left flex items-center justify-between gap-2"
      >
        <span className="truncate text-slate-700">
          {value.length === 0 ? (
            <span className="text-slate-400">{placeholder || 'Select markets…'}</span>
          ) : (
            value.join(', ')
          )}
        </span>
        <ChevronDown size={14} className="text-slate-400 flex-none" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-80 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-slate-100">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search countries"
              className="input py-1 text-sm"
            />
          </div>
          <div className="overflow-y-auto py-1">
            {filtered.map((c) => {
              const on = selected.has(c.code);
              return (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => toggle(c.code)}
                  className={clsx(
                    'w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm hover:bg-slate-50',
                    on && 'bg-brand-purple-50/60',
                  )}
                >
                  <span className="text-slate-700">
                    <span className="font-mono text-[11px] text-slate-400 mr-2">{c.code}</span>
                    {c.name}
                  </span>
                  {on && <Check size={14} className="text-brand-purple-600" />}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-xs text-slate-400">No matches</div>
            )}
          </div>
          <div className="border-t border-slate-100 p-2 flex justify-between">
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-brand-purple-600 hover:text-brand-purple-800"
            >
              Done
            </button>
          </div>
        </div>
      )}
      {value.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {value.map((code) => (
            <span
              key={code}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-xs text-slate-700"
            >
              {code}
              <button
                type="button"
                onClick={() => toggle(code)}
                className="text-slate-400 hover:text-rose-500"
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
